import { OnboardingProductType } from "@redotech/cockroachdb-codegen/congodb/onboarding-product-type";
import { OnboardingType } from "@redotech/cockroachdb-codegen/congodb/onboarding-type";
import {
  createStringEnumSchema,
  selfServeOnboardingStepsConfigurationFieldSchemas,
} from "@redotech/redo-onboarding-common/self-serve-onboarding-configuration";
import * as z from "zod";

const productTypeSchema = createStringEnumSchema(OnboardingProductType);

const stepsConfigurationSchema = z.object({
  steps: z.record(z.string(), z.array(z.string())),
  autoPush: z.boolean(),
  ...selfServeOnboardingStepsConfigurationFieldSchemas,
  customCoveragePrice: z.number().int().nullish(),
  onboardingType: createStringEnumSchema(OnboardingType).nullish(),
});

const createdBySchema = z.object({
  userId: z.string().optional(),
  name: z.string().optional(), // Optional - may not be populated
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  department: z.string().optional(),
});

const assigneeSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(), // Optional - may not be in employees collection
  lastName: z.string().optional(),
  department: z.string().optional(),
  name: z.string().optional(), // Computed full name
});

const hubspotMetadataSchema = z.object({
  dealId: z.string().nullable(),
  companyId: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
});

const onboardingStatusSchema = z.enum([
  "not-started",
  "in-progress",
  "completed",
]);

// Onboarding progress details from masters collection
const onboardingProgressDetailsSchema = z.object({
  status: onboardingStatusSchema,
  completionPercent: z.number().nullable(),
  completionDate: z.string().nullable(),
  totalSessions: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  lastUpdatedAt: z.string().nullable(),
});

// Single configuration document (flat structure)
const configurationSchema = z.object({
  merchantId: z.string().nullable(), // null for pre-install configs
  merchantName: z.string(),
  product: productTypeSchema,
  stepsConfig: stepsConfigurationSchema,
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: createdBySchema.optional(), // Optional for legacy records
  notes: z.string().nullish(),
  onboardingStatus: onboardingStatusSchema,
  onboardingProgress: onboardingProgressDetailsSchema.optional(),
  assignees: z.array(assigneeSchema).optional().default([]),
  publicDomain: z.string().optional(), // for pre-install configs
  hubspotMetadata: hubspotMetadataSchema.optional(), // HubSpot integration metadata
});

// Summary for list view (same as full config now)
const configurationSummarySchema = z.object({
  merchantId: z.string().nullable(), // null for pre-install configs
  merchantName: z.string(),
  product: productTypeSchema,
  active: z.boolean(),
  stepsCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: createdBySchema.optional(),
  onboardingStatus: onboardingStatusSchema,
  assignees: z.array(assigneeSchema).optional().default([]),
  publicDomain: z.string().optional(), // for pre-install configs
});

// Get single configuration by merchantId + product
export const getOnboardingConfiguration = {
  name: "getOnboardingConfiguration" as const,
  input: z.object({
    merchantId: z.string().min(1),
    product: productTypeSchema,
  }),
  output: z.object({
    found: z.boolean(),
    configuration: configurationSchema.nullable(),
  }),
};

// Get single configuration by publicDomain + product (for pre-install configs)
export const getOnboardingConfigurationByDomain = {
  name: "getOnboardingConfigurationByDomain" as const,
  input: z.object({
    publicDomain: z.string().min(1),
    product: productTypeSchema,
  }),
  output: z.object({
    found: z.boolean(),
    configuration: configurationSchema
      .extend({
        // For pre-install configs, merchantId can be null
        merchantId: z.string().nullable(),
        publicDomain: z.string().optional(),
      })
      .nullable(),
  }),
};

// List all configurations
export const getOnboardingConfigurations = {
  name: "getOnboardingConfigurations" as const,
  input: z.object({
    product: productTypeSchema.optional(),
    limit: z.number().min(1).max(100).default(50),
    skip: z.number().min(0).default(0),
    searchQuery: z.string().optional(),
  }),
  output: z.object({
    configurations: z.array(configurationSummarySchema),
    pagination: z.object({
      total: z.number(),
      limit: z.number(),
      skip: z.number(),
      hasMore: z.boolean(),
    }),
  }),
};

export type ProductType = z.infer<typeof productTypeSchema>;
export type StepsConfiguration = z.infer<typeof stepsConfigurationSchema>;
export type Configuration = z.infer<typeof configurationSchema>;
export type ConfigurationSummary = z.infer<typeof configurationSummarySchema>;
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;
export type GetOnboardingConfigurationInput = z.infer<
  typeof getOnboardingConfiguration.input
>;
export type GetOnboardingConfigurationOutput = z.infer<
  typeof getOnboardingConfiguration.output
>;
export type GetOnboardingConfigurationsInput = z.infer<
  typeof getOnboardingConfigurations.input
>;
export type GetOnboardingConfigurationsOutput = z.infer<
  typeof getOnboardingConfigurations.output
>;
export type GetOnboardingConfigurationByDomainInput = z.infer<
  typeof getOnboardingConfigurationByDomain.input
>;
export type GetOnboardingConfigurationByDomainOutput = z.infer<
  typeof getOnboardingConfigurationByDomain.output
>;
